import { useState } from 'react';
import { Currency } from '../types/master-data.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Edit, Trash2, Star, History } from 'lucide-react';
import { formatNumber } from '@/lib/utils/format';
import { ExchangeRateHistoryDialog } from './exchange-rate-history-dialog';

const SYNCED_CURRENCIES = ['USDT', 'USDC'];

interface CurrencyTableProps {
  currencies: Currency[];
  onEdit: (currency: Currency) => void;
  onDelete: (currency: Currency) => void;
  isLoading?: boolean;
}

export function CurrencyTable({ currencies, onEdit, onDelete, isLoading }: CurrencyTableProps) {
  const [historyCode, setHistoryCode] = useState<string | null>(null);
  const [historyName, setHistoryName] = useState<string>('');
  const [historyOpen, setHistoryOpen] = useState(false);

  const handleViewHistory = (currency: Currency) => {
    setHistoryCode(currency.code);
    setHistoryName(currency.name);
    setHistoryOpen(true);
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Cargando divisas...</p>
      </div>
    );
  }

  if (!currencies || currencies.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground font-medium">No hay divisas configuradas</p>
        <p className="text-xs text-muted-foreground mt-1">
          Agrega divisas para poder asignar monedas a tus cotizaciones
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Código</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead>Símbolo</TableHead>
            <TableHead className="text-right">Tasa de Cambio</TableHead>
            <TableHead>SAP</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {currencies.map((currency) => (
            <TableRow key={currency.id}>
              <TableCell className="font-mono font-semibold">
                <div className="flex items-center gap-2">
                  {currency.code}
                  {currency.isBase && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                      </TooltipTrigger>
                      <TooltipContent>Moneda base del sistema</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TableCell>
              <TableCell>{currency.name}</TableCell>
              <TableCell className="font-semibold">{currency.symbol}</TableCell>
              <TableCell className="text-right font-mono">
                {formatNumber(currency.exchangeRate, 4)}
              </TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">
                {currency.sapCode || '—'}
              </TableCell>
              <TableCell>
                <Badge variant={currency.isActive ? 'success' : 'default'}>
                  {currency.isActive ? 'Activa' : 'Inactiva'}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  {SYNCED_CURRENCIES.includes(currency.code) && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewHistory(currency)}
                        >
                          <History className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Ver historial de tipo de cambio con gráfico y detalle por fecha
                      </TooltipContent>
                    </Tooltip>
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="ghost" onClick={() => onEdit(currency)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar divisa</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDelete(currency)}
                        disabled={currency.isBase}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {currency.isBase ? 'No se puede eliminar la moneda base' : 'Eliminar divisa'}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ExchangeRateHistoryDialog
        currencyCode={historyCode}
        currencyName={historyName}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />
    </div>
  );
}

export default CurrencyTable;
